/**
 * What a request in words changed, said in the board's own words.
 *
 * The model that reads the words can misread them -- drop a player it was not asked to,
 * or tick a box nobody named -- so the board never takes its answer on trust: it compares the
 * roster and settings sent with those that came back and says every difference, named as the
 * toolbar names it. What the reader sees is what changed, not what the model says it did.
 */

import { ARRANGE_LAYERS, type AskSettings, type Seat } from "@app/shared-types";
import {
  type Box,
  choiceLabel,
  counterLabel,
  levelLabel,
  textureLabel,
  varyLabel,
} from "../components/ArrangeChoices";
import { LAYER_NAMES, MORE_NAMES } from "../components/ArrangeLayers";
import { presetOf, seatLabel } from "../components/ArrangeRoster";
import { START_NAMES } from "../components/ArrangeStartMenu";
import { EASE_NAMES, keySaid, PLAYING_NAMES, tempoSaid } from "../components/ArrangeWishes";
import { tally } from "./roster";

/** A roster and its settings, as sent or as answered. */
export interface Asked {
  readonly roster: readonly string[];
  readonly settings: AskSettings;
}

const BOXES: readonly Box[] = ["follow", "rise", "moving", "stops", "double", "share"];

/** Each instrument's label, with how many where more than one: "Flute, Horn ×2". */
function named(counts: ReadonlyMap<string, number>, seats: readonly Seat[]): string {
  return [...counts]
    .map(([seat, count]) => `${seatLabel(seat, seats)}${count > 1 ? ` ×${count}` : ""}`)
    .join(", ");
}

/** The players put in and taken off, or nothing where the roster is the same. */
function players(before: Asked, after: Asked, seats: readonly Seat[]): string[] {
  const was = tally(before.roster);
  const now = tally(after.roster);
  const added = new Map<string, number>();
  const taken = new Map<string, number>();
  for (const [seat, count] of now) {
    const more = count - (was.get(seat) ?? 0);
    if (more > 0) added.set(seat, more);
  }
  for (const [seat, count] of was) {
    const fewer = count - (now.get(seat) ?? 0);
    if (fewer > 0) taken.set(seat, fewer);
  }
  const said: string[] = [];
  if (added.size > 0) said.push(`Added ${named(added, seats)}`);
  if (taken.size > 0) said.push(`Took off ${named(taken, seats)}`);
  const preset = presetOf(after.roster);
  if (said.length > 0 && preset !== "" && preset !== presetOf(before.roster)) {
    said.push(`now a ${preset.toLowerCase()}`);
  }
  return said;
}

/** Every difference between what was sent and what came back, one phrase each. */
export function askSummary(before: Asked, after: Asked, seats: readonly Seat[]): string[] {
  const was = before.settings;
  const now = after.settings;
  const said = players(before, after, seats);
  if (now.harmony !== was.harmony) said.push(`Harmony: ${levelLabel(now.harmony)}`);
  if (now.texture !== was.texture) said.push(`Texture: ${textureLabel(now.texture)}`);
  if (now.counter !== was.counter) said.push(`Counter: ${counterLabel(now.counter)}`);
  if (now.vary !== was.vary) said.push(`Tune: ${varyLabel(now.vary)}`);
  for (const box of BOXES) {
    if (now[box] !== was[box]) said.push(`${choiceLabel(box)} ${now[box] ? "on" : "off"}`);
  }
  const bars =
    now.whole !== was.whole || (!now.whole && (now.first !== was.first || now.last !== was.last));
  if (bars) said.push(now.whole ? "Whole piece" : `Bars ${now.first}–${now.last}`);
  if (now.start !== was.start) said.push(`Start: ${START_NAMES[now.start].label}`);
  // What the note model is told is said only where it is started from: elsewhere it is unsent.
  if (now.start === "model") {
    if (ARRANGE_LAYERS.some((layer) => now.layers.includes(layer) !== was.layers.includes(layer))) {
      const told = now.layers.map((layer) => LAYER_NAMES[layer].label.toLowerCase());
      said.push(`Told: ${told.join(", ") || "nothing of the score"}`);
    }
    if (now.more !== was.more) {
      said.push(now.more === null ? "More notes off" : `More notes: ${MORE_NAMES[now.more].label}`);
    }
    if (now.strength !== was.strength) said.push(`Strength ${Math.round(now.strength * 100)}%`);
  }
  if (JSON.stringify(now.key) !== JSON.stringify(was.key)) {
    said.push(now.key === null ? "Key as written" : `Key: ${keySaid(now.key)}`);
  }
  if (JSON.stringify(now.tempo) !== JSON.stringify(was.tempo)) {
    said.push(now.tempo === null ? "Tempo as written" : `Tempo: ${tempoSaid(now.tempo)}`);
  }
  const who = now.playingWho.map((seat) => seatLabel(seat, seats)).join(", ");
  if (now.playing !== was.playing || now.playingWho.join() !== was.playingWho.join()) {
    said.push(`Playing: ${PLAYING_NAMES[now.playing].label}${who === "" ? "" : ` (${who})`}`);
  }
  if (now.ease !== was.ease) said.push(`Ease: ${EASE_NAMES[now.ease].label}`);
  return said;
}
